// src/pages/HomePage.jsx

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AccordionSection from "../components/AccordionSection";
import LoadingSpinner from "../components/LoadingSpinner";
import { fetchItems } from "../api";
import { imgurSize, imgurSrcSet } from "../utils/imgur";
import { FaInstagram } from "react-icons/fa";
import "../App.css";

function HomePage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openExhibition, setOpenExhibition] = useState("");

  /* Uses the shared client in api.js rather than a local API_BASE. The old
     constant hardcoded "http://localhost:8000/api", which on a phone resolves
     to the phone itself.

     Also drops the anonymous Firebase sign-in that ran before this fetch:
     /api/items is public, so it was an extra round trip on the slowest
     connection in the funnel. */
  useEffect(() => {
    let cancelled = false;

    fetchItems()
      .then((data) => {
        if (!cancelled) setItems(data);
      })
      .catch((err) => {
        console.error("❌ Failed to fetch items:", err);
        if (!cancelled) {
          setError(
            err.status === 0
              ? "Could not reach the gallery. Check your connection and try again."
              : "The artworks could not be loaded just now. Please refresh in a moment."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleScrollTo = (id) => {
    setOpenExhibition(id);
    setTimeout(() => {
      const section = document.getElementById(`${id}-accordion`);
      // scrollIntoView is a JS API, so the prefers-reduced-motion rule in
      // base.css doesn't cover it — check the preference explicitly.
      const reduceMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      section?.scrollIntoView({ behavior: reduceMotion ? "auto" : "smooth" });
    }, 100);
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="homepage-container">
      {/* 🔹 Hero Tiles */}
      <section className="hero-tile-wrapper">
        <div className="jewels-tile">
          <h2>Jewels of Cape Town</h2>
          <p>View and Bid on artworks that celebrate the soul of Cape Town.</p>
          <button onClick={() => handleScrollTo("jewels")} className="hero-button">
            Explore Jewels
          </button>
        </div>

        <div className="jewels-tile kruger-tile">
          <h2>Footprints of the Kruger</h2>
          <p>View and Bid on South Africa’s wild heart and keepers of the kingdom.</p>
          <button onClick={() => handleScrollTo("kruger")} className="hero-button">
            Explore Footprints
          </button>
        </div>
      </section>

      {/* 🔹 Narrative Accordions
          className="prose" caps paragraphs at --measure (68ch). Without it the
          artist statement runs to ~120 characters per line on a laptop. */}
      <div className="exhibition-intro prose">
        <AccordionSection title="Soul Meets Story. Pain Becomes Beauty.">
          <p>Welcome to my creative sanctuary, where life's trials are transformed into expressions of beauty and resilience. Discover the Art of Transmuting Soul into Story. I invite you to join me on a journey of metamorphosis—where personal experiences and challenges are transmuted into art that speaks to my soul.</p>

          <p>You’ve just stepped into a sacred space—where pain becomes pigment, stories become symbols, and life itself becomes a living canvas. I am an artist, transmuting wounds into wisdom, shadows into light, and experience into expression. This is not just art; it’s transformation. Every creation is a mirror of the inner journey—a whisper from the soul, a conversation with the Divine, a celebration of resilience, beauty, and becoming.</p>

          <p>Through my work, I aim to inspire and connect, offering reflections that resonate with our shared human experience. Engaging in art as a form of therapy has been shown to enhance self-awareness and emotional resilience. I encourage you to explore the galleries, delve into the stories behind each piece, and perhaps find echoes of your own journey within them.</p>

          <p>Come with me. Let’s walk the golden thread from brokenness to creator. Let my work stir something ancient in you—a remembering, a release, a rebirth. Welcome to my journey.</p>
        </AccordionSection>

        <AccordionSection title="The Living Tapestry. Weaving Stories Through Art.">
          <p>
            Welcome to the <strong>The Living Tapestry</strong>, a personal journey of filling my cup through the heart and soul of art and creation.
          </p>

          <p>
            Each exhibition serves as a distinct thread, intertwining personal experiences with broader cultural narratives. This evolving collection invites viewers to journey through various landscapes and stories, witnessing the transformation of life's challenges into expressions of beauty and resilience.
          </p>

          <p>
            From the shimmering soul of <strong>The Jewels of Cape Town</strong> to the grounded grace of <strong>The Footprints of the Kruger</strong>, each thread captures a facet of the sacred conversation between inner transformation and outer landscape. As each new thread is woven into <strong>The Living Tapestry</strong>, the collection grows richer, offering deeper insights into the transformative power of art and the shared human experience.
          </p>

          <p>
            This tapestry is not just art—it is memory, prayer, emotion, and alchemy. A weaving of pain and beauty, land and spirit, silence and story. As time unfolds, more threads will be revealed—each one a unique portal into the essence of place, presence, and becoming.
          </p>

          <p>
            Welcome to the unfolding.
          </p>
        </AccordionSection>

        <AccordionSection title="Threads of The Living Tapestry">
          <p>
            The Living Tapestry is an evolving collection of exhibitions, each serving as a distinct thread that weaves together personal experiences, emotions, and reflections. This tapestry invites viewers to journey through various landscapes and narratives, witnessing the transformation of life's challenges into expressions of beauty and resilience.
          </p>

          <p>
            The inaugural exhibition, <strong>The Jewels of Cape Town</strong>, delved into the heart and soul of Cape Town, capturing its vibrant essence and multifaceted beauty. Through art, the jewels—mountain majesty, floral kingdom, keepers of the kingdom, ocean's embrace, cultural mosaic—help me explore personal connections to the city, highlighting how its landscapes and culture have shaped my individual experiences and contributed to personal growth. <em>Remembering &amp; Reclaiming.</em>
          </p>

          <p>
            Currently unfolding, <strong>The Footprints of the Kruger</strong> traverses the expansive terrains of the Kruger National Park. It reflects on the profound imprints left by nature and wildlife, drawing parallels between the untamed wilderness and the human journey. The artworks aim to convey the raw beauty and inherent challenges of coexisting with nature, emphasizing themes of survival, adaptation, and harmony. <em>Grounding &amp; Listening.</em>
          </p>

          <p>
            Looking ahead, <strong>The Language of Infinity</strong> aspires to delve into the boundless realms of the cosmos and the infinite possibilities of existence. It seeks to transcend earthly experiences, exploring abstract concepts of eternity, interconnectedness, and the universal language that binds all forms of life. Through this thread, the tapestry will venture into the metaphysical, inviting viewers to contemplate their place within the infinite continuum of time and space. <em>Visioning &amp; Expanding.</em>
          </p>
        </AccordionSection>

        <AccordionSection title="About the Artist">
          <p>
            <em>“The artist is an alchemist who transmutes pain into beauty.”</em>
          </p>

          <p>
            This philosophy resonates deeply with my creative journey, where art becomes a magical and transformative process of converting personal trials into expressions of resilience and grace. I seek to bridge the tangible and the transcendent, allowing each creation to serve as a conduit to creativity.
          </p>

          <p>
            I am driven by a deep desire to create purposeful, sustainable impact that resonates across time. Art is the vessel through which I explore, question, and express. It fuels my curiosity and expands the way I see the world—offering fresh perspectives that inform both my professional path and personal journey. With a unique lens that blends numbers, nature, and nuance, I bridge analytical thinking with soulful creativity. My work is a dialogue between structure and spirit, where insight becomes inspiration and vision transforms into visual expression.
          </p>

          {/* imgurSize(…, "l") requests the 640px variant instead of the 1.4MB
              original — this renders at 400px. width/height reserve space
              before it decodes so the text below doesn't jump. */}
          <img
            src={imgurSize("https://i.imgur.com/ChRqV9S.png", "l")}
            alt="Marcelle Phillipe"
            className="artist-photo"
            width={600}
            height={600}
            loading="lazy"
            decoding="async"
          />

          <p className="follow-line">
            <span>Follow me </span>
            <a
              href="https://www.instagram.com/marcelle_phillipe_za"
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-link"
            >
              <FaInstagram size={18} aria-hidden="true" />
              @marcelle_phillipe_za
            </a>
          </p>
        </AccordionSection>

        <AccordionSection title="The Call to Purposeful Action">
          <p>
            In what ways can the urgently needed inspiration be provided to the South African education landscape? <strong>Calling Education</strong>, with its audacious vision, aims to develop the most relevant model of providing top-quality education to learners from low-income communities in South Africa. <em>“Nothing in our time is more urgently needed, timely, and hopeful than this initiative.”</em>
          </p>

          <p>
            Calling Education has demonstrated that it is possible to operate an independent high school that delivers top-notch education at an annual fee of approximately R7,000, enabling them to reach low-income communities in South Africa.
          </p>

          <p>
            A portion of the artwork sales goes to Calling Education.
          </p>

          <p>
            <em>“Calling Academy is not merely an educational institution; it is a movement. Join me and make a lasting impact on South Africa.”</em>
          </p>

          <div className="partner-block">
            {/* Left unresized — this logo is already 7.6kB, which is what a
                right-sized asset looks like. */}
            <img
              src="https://i.imgur.com/zydIb6a.png"
              alt="Calling Education"
              className="partner-logo"
              width={100}
              height={100}
              loading="lazy"
              decoding="async"
            />
            <a
              href="https://www.callingeducation.org.za/"
              target="_blank"
              rel="noopener noreferrer"
              className="partner-link"
            >
              Visit Calling Education
            </a>
          </div>
        </AccordionSection>
      </div>

      {/* 🔹 Art Grid — Jewels */}
      <AccordionSection
        title="The Jewels of Cape Town"
        isOpen={openExhibition === "jewels"}
        id="jewels-accordion"
        onToggle={() => setOpenExhibition(openExhibition === "jewels" ? "" : "jewels")}
      >
        {error ? (
          <p className="error-text" role="alert">{error}</p>
        ) : items.length === 0 ? (
          <p className="coming-soon-text">No artworks are listed yet — please check back soon.</p>
        ) : (
          <div className="art-grid">
            {items.map((item, index) => (
              /* Exactly seven children, in this order, on every card. The grid
                 uses `grid-template-rows: subgrid`, so each card shares the
                 parent's row bands — that's what aligns titles, descriptions
                 and buttons across cards. Omit or reorder one and everything
                 after it shifts up a row. */
              <div className="art-card" key={item.id}>
                {/* 1 — image.
                    The originals average 4MB; these cards render at 280-400px.
                    srcSet lets the browser choose the 320px or 640px variant by
                    viewport and pixel density. Grid total drops from ~45MB to
                    around 1MB. */}
                <img
                  src={imgurSize(item.images?.[0], "m")}
                  srcSet={imgurSrcSet(item.images?.[0], ["m", "l"])}
                  sizes="(max-width: 600px) 90vw, (max-width: 1000px) 45vw, 300px"
                  alt={`${item.title} by ${item.artist}`}
                  width={800}
                  height={600}
                  loading={index < 3 ? "eager" : "lazy"}
                  decoding="async"
                />

                {/* 2 — title */}
                <h3>{item.title}</h3>

                {/* 3 — artist */}
                <p><em>by {item.artist}</em></p>

                {/* 4 — description */}
                <p className="short-desc">{item.short_description}</p>

                {/* 5 — tags */}
                <div className="jewel-tags">
                  {item.jewels.map((jewel, idx) => (
                    <span className="jewel-badge" key={idx}>{jewel}</span>
                  ))}
                </div>

                {/* 6 — badge slot; rendered even when empty to hold the row */}
                <div className="badge-slot">
                  {!item.for_sale && (
                    <span className="exhibition-badge">Exhibition only</span>
                  )}
                </div>

                {/* 7 — CTA. aria-label names the artwork: a screen-reader user
                    listing links would otherwise hear "View" twelve times. */}
                <Link
                  to={`/auction/item/${item.id}`}
                  className="cta-button"
                  aria-label={`View ${item.title}`}
                >
                  View
                </Link>
              </div>
            ))}
          </div>
        )}
      </AccordionSection>

      {/* 🔹 Kruger Placeholder */}
      <AccordionSection
        title="Footprints of the Kruger"
        isOpen={openExhibition === "kruger"}
        id="kruger-accordion"
        onToggle={() => setOpenExhibition(openExhibition === "kruger" ? "" : "kruger")}
      >
        <div className="coming-soon-text">
          <p>
            This thread is in progress and will feature new artworks and immersive moments from the heart of Kruger National Park. Stay tuned for the next chapter in the living tapestry of my artistic journey.
          </p>
        </div>
      </AccordionSection>
    </div>
  );
}

export default HomePage;